import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

export interface CustomErrorContext {
  feature?: string;
  action?: string;
  userId?: string;
  sessionId?: string;
  additionalData?: any;
}

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  // Capture custom exceptions with context
  captureException(
    error: Error,
    context?: CustomErrorContext,
    level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'error',
  ): void {
    Sentry.withScope((scope) => {
      scope.setLevel(level);

      if (context) {
        this.setCustomContext(scope, context);
      }

      Sentry.captureException(error);
    });
  }

  // Capture custom messages
  captureMessage(
    message: string,
    context?: CustomErrorContext,
    level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
  ): void {
    Sentry.withScope((scope) => {
      scope.setLevel(level);

      if (context) {
        this.setCustomContext(scope, context);
      }

      Sentry.captureMessage(message, level);
    });
  }

  // Capture business logic errors
  captureBusinessError(errorType: string, details: any, context?: CustomErrorContext): void {
    Sentry.withScope((scope) => {
      scope.setTag('error.category', 'business_logic');
      scope.setTag('error.type', errorType);
      scope.setLevel('warning');

      scope.setContext('Business Error Details', details);

      if (context) {
        this.setCustomContext(scope, context);
      }

      Sentry.captureMessage(`Business Logic Error: ${errorType}`, 'warning');
    });
  }

  // Capture performance issues
  capturePerformanceIssue(operation: string, duration: number, threshold: number = 5000): void {
    if (duration > threshold) {
      Sentry.withScope((scope) => {
        scope.setTag('performance.issue', 'slow_operation');
        scope.setContext('Performance Issue', {
          operation,
          duration,
          threshold,
          timestamp: new Date().toISOString(),
        });
        scope.setLevel('warning');

        Sentry.captureMessage(`Slow operation detected: ${operation} took ${duration}ms`, 'warning');
      });
    }
  }

  // Set user context
  setUser(user: { id?: string; email?: string; username?: string }): void {
    Sentry.setUser(user);
  }

  // Add breadcrumb for debugging
  addBreadcrumb(message: string, category: string, data?: any): void {
    Sentry.addBreadcrumb({
      message,
      category,
      data,
      level: 'info',
      timestamp: Date.now() / 1000,
    });
  }

  // Set custom tags
  setTag(key: string, value: string): void {
    Sentry.setTag(key, value);
  }

  // Start a transaction for performance monitoring
  startTransaction(name: string, operation: string) {
    this.addBreadcrumb(`Starting transaction: ${name}`, 'performance', {
      name,
      operation,
      timestamp: new Date().toISOString(),
    });

    return {
      name,
      operation,
      startTime: Date.now(),
      finish: () => {
        this.addBreadcrumb(`Finished transaction: ${name}`, 'performance', {
          name,
          operation,
          duration: Date.now() - Date.now(),
          timestamp: new Date().toISOString(),
        });
      },
    };
  }

  // Clear user context
  clearUser(): void {
    Sentry.setUser(null);
  }

  // Set application context
  setAppContext(context: Record<string, any>): void {
    Sentry.setContext('Application Context', context);
  }

  // Capture route change for navigation tracking
  captureRouteChange(from: string, to: string): void {
    this.addBreadcrumb(`Route change: ${from} → ${to}`, 'navigation', {
      from,
      to,
      timestamp: new Date().toISOString(),
    });
  }

  // Capture API response times
  captureApiPerformance(endpoint: string, method: string, duration: number, status: number): void {
    if (duration > 2000) {
      Sentry.withScope((scope) => {
        scope.setTag('api.slow', 'true');
        scope.setContext('API Performance', {
          endpoint,
          method,
          duration,
          status,
          timestamp: new Date().toISOString(),
        });
        scope.setLevel('warning');

        Sentry.captureMessage(`Slow API call: ${method} ${endpoint} took ${duration}ms`, 'warning');
      });
    }
  }

  private setCustomContext(scope: any, context: CustomErrorContext): void {
    if (context.feature) scope.setTag('feature', context.feature);
    if (context.action) scope.setTag('action', context.action);
    if (context.userId) scope.setUser({ id: context.userId });
    if (context.sessionId) scope.setTag('session.id', context.sessionId);
    if (context.additionalData) {
      scope.setContext('Additional Data', context.additionalData);
    }
  }
}
